// HallmarkRegistration.jsx
import React from "react";

export default function HallmarkRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            Hallmark Registration
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            BIS Hallmark Registration for Jewellers
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks supports jewellers in obtaining Hallmark
            Registration under BIS guidelines, ensuring compliance for selling
            hallmarked gold and silver jewellery across sales outlets.
          </p>
          <p className="text-gray-700">
            The registration process is handled through the BIS Manak Online
            portal with complete document preparation, site inspection
            coordination, and compliance verification for seamless BIS approval.
          </p>
        </div>

        {/* Right: Image (BIS HALLMARK REGISTRATION - Jewellery shop/gold silver compliance) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/325153/pexels-photo-325153.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Jeweller obtaining BIS Hallmark Registration"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides end-to-end assistance for BIS
              Hallmark Registration, helping jewellers meet mandatory BIS
              standards for selling hallmarked gold and silver jewellery.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Eligibility assessment for BIS Hallmark Registration and sales
                  outlet-specific compliance requirements.
                </li>
                <li>
                  Complete online application preparation through BIS Manak
                  portal with Form-I submission and fee payment management.
                </li>
                <li>
                  Document verification and structuring to meet BIS scrutiny
                  standards for jeweller registration certificates.
                </li>
                <li>
                  Coordination for BIS site inspections and resolution of any
                  compliance observations or queries raised.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for Hallmark Registration
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling all mandatory BIS
              documents required for jeweller hallmark registration approval.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Business registration certificate (Incorporation, Partnership Deed, or Proprietorship declaration).</li>
                <li>PAN card of the business entity and authorised signatory.</li>
                <li>
                  Premises address proof (GST certificate, rent agreement,
                  property tax receipt, or utility bills).
                </li>
                <li>
                  Proof of annual turnover through GST returns or CA-certified
                  financial statements for the previous financial year.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm delivers specialised BIS compliance expertise with proven
              success in securing Hallmark Registration for jewellers nationwide.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  BIS Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Deep knowledge of BIS Hallmarking Scheme regulations, jeweller
                  registration requirements, and mandatory hallmarking standards.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Application Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional handling of BIS online portal applications,
                  document uploads, and site inspection preparation for approvals.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Advisory Services
                </h3>
                <p className="text-gray-700 text-sm">
                  Ongoing compliance guidance for renewals, hallmarking centre
                  usage, and BIS audit requirements to maintain registration validity.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Get BIS Hallmark Registration Approved
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to complete your BIS Hallmark
                Registration and legally sell hallmarked gold and silver jewellery
                with consumer trust and regulatory compliance.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
